import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Tabs, type TabItem } from '@domcrm/ui';
import {
  getAuthCallStatus,
  isAuthContextRequired,
  postAuthRequestCall,
  postAuthLogin2FA,
  postAuthSelectContext,
  postPublicResendEmailVerification,
  type LoginContextOption
} from '../../entities/me/me.api';
import { ApiError } from '../../shared/api/api-error';
import { useAuth } from '../../shared/auth/use-auth';
import {
  normalizePhone,
  validateEmail,
  validatePhoneRu
} from '../../shared/lib/phone-ru';
import { userFacingActionError } from '../../shared/lib/staff-user-facing-error';
import { ErrorText } from '../../shared/ui/ErrorText';
import { PasswordInput } from '../../shared/ui/password-input';
import { PhoneInput } from '../../shared/ui/PhoneInput';
import { useStaffToast } from '../../shared/ui/StaffToast';
import { PublicOnlyRoute } from '../../app/router/ProtectedRoute';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import { StaffThemeToggle } from '../../shared/theme/StaffThemeToggle';
import { useSmartCaptcha } from '../../shared/smartcaptcha/smartcaptcha';
import { SmartCaptchaNotice } from '../../shared/smartcaptcha/SmartCaptchaNotice';

/** Резервный код 2FA: буквы/цифры, формат XXXXX-XXXXX (дефис ставим сами). */
function normalizeRecoveryCodeInput(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
  return clean.length > 5 ? `${clean.slice(0, 5)}-${clean.slice(5)}` : clean;
}

/** Сообщение под кнопкой «Войти»: без дублирования английских строк API. */
function passwordLoginSubmitErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.errorType === 'no_staff_role') {
      return 'Клиентский профиль не может входить в CRM. Используйте приложение студии';
    }
    if (err.errorType === 'no_role_in_organization') {
      return 'У вас нет роли в этой студии';
    }
    if (err.errorType === 'client_self_join_staff_only') {
      return 'Студия принимает новых клиентов только через администратора';
    }
    if (err.errorType === 'staff_client_forbidden') {
      const d = (err.detail ?? '').trim();
      return d || 'Клиентский профиль не может входить в админку. Используйте виджет организации.';
    }
    if (err.errorType === 'auth_invalid') {
      return 'Неверный email, телефон или пароль';
    }
    if (err.errorType === 'login_failed') {
      return 'Не удалось завершить вход. Попробуйте позже.';
    }
    if (err.errorType === 'phone_not_verified') {
      return 'Сначала войдите по номеру телефона, чтобы подтвердить его.';
    }
    if (err.errorType === 'email_not_verified') {
      return err.detail || 'Проверьте почту и подтвердите email';
    }
    const raw = (err.detail ?? '').trim();
    if (/incorrect email.*(phone|password)|incorrect.*(phone|email)/i.test(raw)) return 'Неверный email, телефон или пароль';
    if (/two-factor authentication is required/i.test(raw)) {
      return 'Для этой учётной записи включена двухфакторная проверка. Вход по коду будет подключён отдельно.';
    }
    if (err.status === 401 && raw && !/^HTTP\s+\d{3}/i.test(raw)) {
      return raw;
    }
    if (err.status === 401) {
      return 'Не удалось войти. Проверьте email, телефон и пароль.';
    }
    if (err.status === 500) {
      return 'Внутренняя ошибка сервера';
    }
  }
  const msg = userFacingActionError(err);
  if (/incorrect email.*(phone|password)|incorrect.*(phone|email)/i.test(msg)) return 'Неверный email, телефон или пароль';
  if (/two-factor authentication is required/i.test(msg)) {
    return 'Для этой учётной записи включена двухфакторная проверка. Вход по коду будет подключён отдельно.';
  }
  return msg;
}

type AuthTab = 'password' | 'call';
/** #__INCOMING_CALL_DISABLE__ — только callcheck: шаги 'phone' → 'callcheck'. */
type CallStep = 'phone' | 'callcheck';

/** Кулдаун повторного письма-верификации; backend rate-limit — не более 10 в час с одного IP. */
const RESEND_COOLDOWN_S = 300;

// #__PLUSOFON_DISABLE__ — оригинальный код временно закомментирован, не удалён.
// /** Допустимые провайдеры callcheck из ответа; если сервер ничего не прислал — по умолчанию только sms_ru. */
// function normalizeCallcheckProviders(list: unknown): string[] {
//   if (!Array.isArray(list)) return ['sms_ru'];
//   const arr = list.filter((x): x is string => x === 'sms_ru' || x === 'plusofon');
//   return arr.length ? Array.from(new Set(arr)) : ['sms_ru'];
// }

export default function LoginPage() {
  const { login, applySessionTokens, apiClient } = useAuth();
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callCheckIdRef = useRef('');
  const contextRevoked =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('context_revoked') === '1';

  const [tab, setTab] = useState<AuthTab>('call');
  const [callStep, setCallStep] = useState<CallStep>('phone');

  const [email, setEmail] = useState('');
  const [accountPhone, setAccountPhone] = useState('');
  const [password, setPassword] = useState('');

  const [callPhone, setCallPhone] = useState('');
  const [callcheckPhone, setCallcheckPhone] = useState('');
  const [callLoading, setCallLoading] = useState(false);
  // #__PLUSOFON_DISABLE__: выбор оператора звонка отключён:
  // const [callProvider, setCallProvider] = useState<'sms_ru' | 'plusofon'>('sms_ru');
  // const [callcheckProviders, setCallcheckProviders] = useState<string[]>(['sms_ru']);
  const [callCooldown, setCallCooldown] = useState(0);

  const [emailErr, setEmailErr] = useState<string | undefined>();
  const [accountPhoneErr, setAccountPhoneErr] = useState<string | undefined>();
  const [passwordErr, setPasswordErr] = useState<string | undefined>();
  const [callPhoneErr, setCallPhoneErr] = useState<string | undefined>();
  const [callSubmitErr, setCallSubmitErr] = useState<string | undefined>();
  const [loginSubmitErr, setLoginSubmitErr] = useState<string | undefined>();
  /** Гейт email: показываем форму повторной отправки письма-верификации. */
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNote, setResendNote] = useState<string | undefined>();
  const [resendCooldown, setResendCooldown] = useState(0);
  const [twofaStep, setTwofaStep] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpRecoveryMode, setTotpRecoveryMode] = useState(false);
  const [totpErr, setTotpErr] = useState<string | undefined>();
  /** context_required: несколько доступных разделов — окно «Войти как». */
  const [contextChoice, setContextChoice] = useState<{
    contexts: LoginContextOption[];
    selectionToken: string;
  } | null>(null);
  const [contextBusyId, setContextBusyId] = useState<string | null>(null);
  const [contextErr, setContextErr] = useState<string | undefined>();

  // Yandex SmartCaptcha (невидимая): включается бэкендом (SMARTCAPTCHA_MODE=required).
  const captcha = useSmartCaptcha();

  const toast = useStaffToast();
  const [pending, setPending] = useState(false);

  function stopPolling(): void {
    if (pollTimerRef.current != null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  useEffect(() => {
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (callCooldown <= 0) return;
    const timer = setInterval(() => {
      setCallCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [callCooldown]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Динамическая проверка доступности входа по Email удалена вместе с методом «Код на email».

  function showContextChoice(contexts: LoginContextOption[], selectionToken: string): void {
    stopPolling();
    callCheckIdRef.current = '';
    setContextChoice({ contexts, selectionToken });
    setContextBusyId(null);
    setContextErr(undefined);
    setLoginSubmitErr(undefined);
    setTotpErr(undefined);
    setTwofaStep(false);
    setTotpCode('');
    setTotpRecoveryMode(false);
    setCallStep('phone');
  }

  async function onSelectContext(context: LoginContextOption): Promise<void> {
    if (!contextChoice) return;
    setContextBusyId(context.id);
    setContextErr(undefined);
    try {
      const tokens = await postAuthSelectContext(apiClient, {
        selection_token: contextChoice.selectionToken,
        context_id: context.id
      });
      if (!tokens.access_token) throw new Error('Не получен access_token');
      setContextChoice(null);
      await applySessionTokens(tokens.access_token, tokens.refresh_token ?? null);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.errorType === 'selection_token_invalid' || err.status === 401) {
          setContextErr('Время выбора истекло. Войдите заново.');
          setContextChoice(null);
        } else if (err.errorType === 'client_self_join_staff_only') {
          setContextErr('Студия принимает новых клиентов только через администратора');
        } else {
          setContextErr(passwordLoginSubmitErrorMessage(err));
        }
      } else {
        setContextErr(userFacingActionError(err));
      }
    } finally {
      setContextBusyId(null);
    }
  }

  function switchTab(next: AuthTab): void {
    stopPolling();
    setEmailErr(undefined);
    setAccountPhoneErr(undefined);
    setPasswordErr(undefined);
    setLoginSubmitErr(undefined);
    setCallPhoneErr(undefined);
    setCallSubmitErr(undefined);
    setEmailNotVerified(false);
    setResendNote(undefined);
    setTab(next);
    if (next === 'call') {
      setCallStep('phone');
      callCheckIdRef.current = '';
      setCallcheckPhone('');
    }
  }

  function validatePasswordField(raw: string): string | undefined {
    const p = (raw ?? '').trim();
    if (!p) return 'Введите пароль.';
    if (p.length > 512) return 'Пароль слишком длинный.';
    return undefined;
  }

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setEmailErr(undefined);
    setAccountPhoneErr(undefined);
    setPasswordErr(undefined);
    setLoginSubmitErr(undefined);
    setEmailNotVerified(false);
    setResendNote(undefined);
    const em = validateEmail(email, { required: true });
    if (em !== true) {
      setEmailErr(em);
      return;
    }
    const ph = validatePhoneRu(accountPhone, { required: true });
    if (ph !== true) {
      setAccountPhoneErr(ph);
      return;
    }
    const pwErr = validatePasswordField(password);
    if (pwErr) {
      setPasswordErr(pwErr);
      return;
    }
    const phoneNorm = normalizePhone(accountPhone);
    setPending(true);
    try {
      const res = await captcha.run((captchaToken) =>
        login({
          email: email.trim().toLowerCase(),
          password: password.trim(),
          phone: phoneNorm,
          surface: 'staff',
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      if (isAuthContextRequired(res)) {
        showContextChoice(res.contexts, res.selection_token);
      }
    } catch (err) {
      if (err instanceof ApiError && err.errorType === 'two_factor_required') {
        setTempToken(err.tempToken || '');
        setTotpRecoveryMode(false);
        setTotpCode('');
        setTotpErr(undefined);
        setTwofaStep(true);
        return;
      }
      if (err instanceof ApiError && err.errorType === 'email_not_verified') {
        // Гейт email: показываем форму повторной отправки письма.
        setEmailNotVerified(true);
        setResendNote(undefined);
        setLoginSubmitErr(undefined);
        return;
      }
      setLoginSubmitErr(passwordLoginSubmitErrorMessage(err));
    } finally {
      setPending(false);
      if (captcha.enabled) captcha.reset();
    }
  }

  async function onResendVerificationEmail(): Promise<void> {
    if (resendBusy || resendCooldown > 0) return;
    const em = email.trim().toLowerCase();
    if (validateEmail(em, { required: true }) !== true) {
      setResendNote('Укажите корректный email.');
      return;
    }
    setResendBusy(true);
    try {
      await postPublicResendEmailVerification(apiClient, em);
      setResendNote('Письмо отправлено. Проверьте почту и подтвердите email.');
      setResendCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setResendNote('Слишком частые попытки. Попробуйте позже.');
        setResendCooldown(RESEND_COOLDOWN_S);
      } else {
        setResendNote(userFacingActionError(err));
      }
    } finally {
      setResendBusy(false);
    }
  }

  async function onTotpSubmit(e: FormEvent) {
    e.preventDefault();
    setTotpErr(undefined);
    const rawCode = totpRecoveryMode
      ? totpCode.replace(/[^A-Z0-9]/g, '')
      : totpCode.trim().replace(/\s+/g, '');
    if (!rawCode) {
      setTotpErr(totpRecoveryMode ? 'Введите резервный код.' : 'Введите код.');
      return;
    }
    if (totpRecoveryMode) {
      if (rawCode.length !== 10) {
        setTotpErr('Резервный код состоит из 10 букв и цифр (XXXXX-XXXXX).');
        return;
      }
    } else if (rawCode.length !== 6) {
      setTotpErr('Код должен состоять из 6 цифр.');
      return;
    }
    const code = totpRecoveryMode ? `${rawCode.slice(0, 5)}-${rawCode.slice(5)}` : rawCode;
    setPending(true);
    try {
      const res = await captcha.run((captchaToken) =>
        postAuthLogin2FA(apiClient, {
          temp_token: tempToken,
          code,
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      if (isAuthContextRequired(res)) {
        showContextChoice(res.contexts, res.selection_token);
        return;
      }
      if (res.access_token) {
        await applySessionTokens(res.access_token, res.refresh_token ?? null);
      } else {
        throw new Error('Не получен access_token');
      }
    } catch (err) {
      setTotpErr(userFacingActionError(err));
    } finally {
      setPending(false);
      if (captcha.enabled) captcha.reset();
    }
  }

  async function pollCallStatus(): Promise<void> {
    const ck = callCheckIdRef.current;
    if (!ck) return;
    try {
      const status = await getAuthCallStatus(apiClient, ck);
      if (status.status === 'pending') return;
      if (status.status === 'two_factor_required') {
        stopPolling();
        callCheckIdRef.current = '';
        setCallStep('phone');
        setTempToken(status.temp_token || '');
        setTotpRecoveryMode(false);
        setTotpCode('');
        setTotpErr(undefined);
        setTwofaStep(true);
        return;
      }
      if (status.status === 'context_required') {
        showContextChoice(status.contexts ?? [], status.selection_token ?? '');
        return;
      }
      if (status.status === 'expired') {
        stopPolling();
        callCheckIdRef.current = '';
        setCallStep('phone');
        toast({ variant: 'error', message: 'Время попытки авторизации истекло. Запросите вход заново.' });
        return;
      }
      if (status.status === 'confirmed') {
        if (status.access_token) {
          stopPolling();
          try {
            await applySessionTokens(status.access_token, status.refresh_token ?? null);
          } catch (err) {
            callCheckIdRef.current = '';
            setCallStep('phone');
            toast({ variant: 'error', message: userFacingActionError(err) });
          }
          return;
        }
        if (status.registration_token) {
          stopPolling();
          callCheckIdRef.current = '';
          setCallStep('phone');
          toast({
            variant: 'error',
            message: 'Эта учётная запись не привязана к сотруднику. Обратитесь к администратору организации.'
          });
          return;
        }
      }
      stopPolling();
      callCheckIdRef.current = '';
      setCallStep('phone');
      toast({ variant: 'error', message: 'Не удалось завершить вход по коду.' });
    } catch (err) {
      stopPolling();
      callCheckIdRef.current = '';
      setCallStep('phone');
      toast({
        variant: 'error',
        message: err instanceof ApiError ? passwordLoginSubmitErrorMessage(err) : userFacingActionError(err)
      });
    }
  }

  async function onCallcheckSubmit(e?: FormEvent): Promise<void> {
    if (e) e.preventDefault();
    setCallPhoneErr(undefined);
    setCallSubmitErr(undefined);
    const ph = validatePhoneRu(callPhone, { required: true });
    if (ph !== true) {
      setCallPhoneErr(ph);
      return;
    }
    await onSwitchToCallcheck();
  }

// #__INCOMING_CALL_DISABLE__ — onRequestRobotCall (входящий flash-call) удалён.
// #__PLUSOFON_DISABLE__ — оригинальный код временно закомментирован, не удалён.
//
//   async function onRetryCall(): Promise<void> {
//     if (callLoading || callCooldown > 0) return;
//     const normalized = normalizePhone(callPhone);
//     const nextProvider = callProvider === 'sms_ru' ? 'plusofon' : 'sms_ru';
//     setCallLoading(true);
//     try {
//       const response = await postAuthRequestCall(apiClient, { phone: normalized, provider: nextProvider });
//       callCheckIdRef.current = response.check_id;
//       setCallPhonePretty(response.call_phone_pretty || response.call_phone || '—');
//       setCallProvider(nextProvider);
//       setCallCooldown(CALL_RESEND_COOLDOWN_S);
//       toast({
//         variant: 'success',
//         message: nextProvider === 'plusofon' ? 'Запрошен повторный звонок через Плюсофон.' : 'Запрошен повторный звонок через SMS.ru.'
//       });
//       stopPolling();
//       pollTimerRef.current = setInterval(() => {
//         void pollCallStatus();
//       }, 1500);
//       void pollCallStatus();
//     } catch (err) {
//       if (err instanceof ApiError && err.status === 429) {
//         toast({ variant: 'error', message: 'Повторный запрос звонка возможен не ранее чем через 3 минуты.' });
//         return;
//       }
//       toast({ variant: 'error', message: userFacingActionError(err) });
//     } finally {
//       setCallLoading(false);
//     }
//   }
//
//   function onCancelCall(): void {
//     stopPolling();
//     callCheckIdRef.current = '';

  async function onSwitchToCallcheck(): Promise<void> {
    if (callLoading) return;
    const normalized = normalizePhone(callPhone);
    if (!normalized) return;
    setCallLoading(true);
    setCallSubmitErr(undefined);
    try {
      const response = await captcha.run((captchaToken) =>
        postAuthRequestCall(apiClient, {
          phone: normalized,
          surface: 'staff',
          call_mode: 'callcheck',
          provider: 'sms_ru',
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      const phoneToCall = response.call_phone_pretty || response.call_phone || '';
      if (!phoneToCall || !/\d/.test(phoneToCall)) {
        setCallSubmitErr('Не удалось получить номер для звонка.');
        return;
      }
      callCheckIdRef.current = response.check_id;
      setCallcheckPhone(phoneToCall);
      setCallStep('callcheck');
      stopPolling();
      pollTimerRef.current = setInterval(() => {
        void pollCallStatus();
      }, 1500);
      void pollCallStatus();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setCallSubmitErr('Превышен лимит запросов. Попробуйте позже.');
        return;
      }
      setCallSubmitErr(userFacingActionError(err));
    } finally {
      setCallLoading(false);
      if (captcha.enabled) captcha.reset();
    }
  }

  function onCancelCallcheck(): void {
    stopPolling();
    callCheckIdRef.current = '';
    setCallStep('phone');
    setCallcheckPhone('');
    setCallCooldown(0);
  }

// #__PLUSOFON_DISABLE__ — оригинальный код временно закомментирован, не удалён.
//   async function onSwitchCallcheckOperator(): Promise<void> {
//     if (callLoading) return;
//     const ph = validatePhoneRu(callPhone, { required: true });
//     if (ph !== true) {
//       setCallStep('phone');
//       setCallPhoneErr(ph);
//       return;
//     }
//     stopPolling();
//     callCheckIdRef.current = '';
//     const nextProvider: 'sms_ru' | 'plusofon' = callProvider === 'sms_ru' ? 'plusofon' : 'sms_ru';
//     const normalized = normalizePhone(callPhone);
//     setCallLoading(true);
//     try {
//       const response = await postAuthRequestCall(apiClient, {
//         phone: normalized,
//         call_mode: 'callcheck',
//         provider: nextProvider
//       });
//       callCheckIdRef.current = response.check_id;
//       setCallcheckPhone(response.call_phone_pretty || response.call_phone || '—');
//       setCallProvider(nextProvider);
//       setCallcheckProviders(normalizeCallcheckProviders(response.callcheck_providers));
//       stopPolling();
//       pollTimerRef.current = setInterval(() => {
//         void pollCallStatus();
//       }, 1500);
//       void pollCallStatus();
//     } catch (err) {
//       if (err instanceof ApiError && err.status === 429) {
//         toast({ variant: 'error', message: 'Повторный запрос звонка возможен не ранее чем через 3 минуты.' });
//         return;
//       }
//       toast({ variant: 'error', message: userFacingActionError(err) });
//     } finally {
//       setCallLoading(false);
//     }
//   }

  const canSubmitPassword = useMemo(() => {
    const emailOk = validateEmail(email, { required: true }) === true;
    const phoneOk = validatePhoneRu(accountPhone, { required: true }) === true;
    return emailOk && phoneOk && password.trim().length > 0;
  }, [email, accountPhone, password]);

  const canSubmitCall = useMemo(() => {
    return validatePhoneRu(callPhone, { required: true }) === true;
  }, [callPhone]);

  const tabItems: TabItem[] = [
    {
      value: 'call',
      label: 'Вход по звонку',
      content: (
          <div className="mt-4 space-y-4">
            {callStep === 'phone' ? (
              <form className="space-y-4" onSubmit={onCallcheckSubmit}>
                <p className="m-0 text-sm text-[hsl(var(--ui-muted-foreground))]">
                  Укажите номер телефона для подтверждения входа. Совершите бесплатный звонок на предложенный номер — звонок сбросится, вход подтвердится автоматически.
                </p>
                <div>
                  <label className="mb-1 block text-xs text-[hsl(var(--ui-muted-foreground))]" htmlFor="call-phone">
                    Телефон
                  </label>
                  <PhoneInput
                    id="call-phone"
                    data-testid="call-phone"
                    autoComplete="tel"
                    placeholder="+7 (999) 123-45-67"
                    aria-invalid={callPhoneErr ? true : undefined}
                    aria-describedby={callPhoneErr ? 'call-phone-err' : undefined}
                    value={callPhone}
                    onChange={(v) => {
                      setCallPhone(v);
                      setCallPhoneErr(undefined);
                      setCallSubmitErr(undefined);
                    }}
                  />
                  {callPhoneErr ? <ErrorText id="call-phone-err">{callPhoneErr}</ErrorText> : null}
                </div>
                <SmartCaptchaNotice
                  enabled={captcha.enabled}
                  challengeVisible={captcha.challengeVisible}
                  status={captcha.status}
                  onRetry={captcha.refresh}
                />
                <Button type="submit" data-testid="call-request" variant="primary" className="w-full" disabled={!canSubmitCall || callLoading || captcha.loading}>
                  {callLoading ? 'Запрашиваем…' : 'Позвонить для входа'}
                </Button>
                {callSubmitErr ? <ErrorText id="call-submit-err">{callSubmitErr}</ErrorText> : null}
                <Link
                  to={ROUTE_PATHS.pricing}
                  data-testid="login-pricing-button"
                  className="inline-flex h-8 items-center justify-center rounded-[var(--ui-radius-control)] px-3 text-[13px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring)/0.35)] disabled:opacity-50 bg-[hsl(var(--ui-primary))] text-[hsl(var(--ui-primary-foreground))] hover:opacity-90 w-full"
                >
                  Тарифы
                </Link>
              </form>
            ) : null}
            {callStep === 'callcheck' ? (
              <div className="space-y-4">
                <p className="m-0 text-sm text-[hsl(var(--ui-muted-foreground))]">
                  Позвоните на этот номер с вашего телефона <strong>{callPhone}</strong> — звонок будет сброшен, вход подтвердится автоматически.
                </p>
                <div className="rounded-md border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-muted))] p-4 text-center">
                  <a
                    href={`tel:${callcheckPhone.replace(/\D/g, '')}`}
                    className="text-2xl font-bold tracking-wider text-[hsl(var(--ui-foreground))] hover:underline"
                  >
                    {callcheckPhone}
                  </a>
                  <p className="mt-1 mb-0 text-xs text-[hsl(var(--ui-muted-foreground))]">
                    Оператор: SMS.ru
                  </p>
                {/*
#__PLUSOFON_DISABLE__: Оператор: {callProvider === 'plusofon' ? 'Плюсофон' : 'SMS.ru'}
*/}
                </div>
                <p className="m-0 text-xs text-[hsl(var(--ui-muted-foreground))]">
                  Звонок бесплатный. Как только вы совершите вызов, он будет сброшен, и вход выполнится автоматически.
                </p>
                {/*
#__PLUSOFON_DISABLE__ — кнопка смены оператора:
{callcheckProviders.includes('sms_ru') && callcheckProviders.includes('plusofon') ? (
  <Button
    type="button"
    variant="secondary"
    className="w-full"
    disabled={callLoading}
    onClick={() => void onSwitchCallcheckOperator()}
  >
    {callLoading ? 'Сменяем оператора…' : 'Сменить оператора'}
  </Button>
) : null}
*/}
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={onCancelCallcheck}
                >
                  Изменить номер
                </Button>
                {/*
#__PLUSOFON_DISABLE__ — прежняя кнопка «Отмена» заменена на «Изменить номер»:
<Button type="button" variant="secondary" className="w-full" onClick={onCancelCallcheck}>
  Отмена
</Button>
*/}
              </div>
            ) : null}
          </div>
      )
    },
    {
      value: 'password',
      label: 'Вход по паролю',
      content: (
          <form className="mt-4 space-y-4" onSubmit={onPasswordSubmit}>
            <div>
              <label className="mb-1 block text-xs text-[hsl(var(--ui-muted-foreground))]" htmlFor="login-email">
                Логин (email)
              </label>
              <Input
                id="login-email"
                data-testid="login-email"
                type="email"
                autoComplete="username"
                aria-invalid={emailErr ? true : undefined}
                aria-describedby={emailErr ? 'login-email-err' : undefined}
                value={email}
                onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                  setEmail(ev.target.value);
                  setEmailErr(undefined);
                  setLoginSubmitErr(undefined);
                  setEmailNotVerified(false);
                  setResendNote(undefined);
                }}
              />
              {emailErr ? (
                <ErrorText id="login-email-err">
                  {emailErr}
                </ErrorText>
              ) : null}
            </div>
            <div>
              <label className="mb-1 block text-xs text-[hsl(var(--ui-muted-foreground))]" htmlFor="login-phone">
                Телефон
              </label>
              <PhoneInput
                id="login-phone"
                data-testid="login-phone"
                autoComplete="tel"
                placeholder="+7 (999) 123-45-67"
                aria-invalid={accountPhoneErr ? true : undefined}
                aria-describedby={accountPhoneErr ? 'login-phone-err' : undefined}
                value={accountPhone}
                onChange={(v) => {
                  setAccountPhone(v);
                  setAccountPhoneErr(undefined);
                  setLoginSubmitErr(undefined);
                }}
              />
              {accountPhoneErr ? <ErrorText id="login-phone-err">{accountPhoneErr}</ErrorText> : null}
            </div>
            <div>
              <label className="mb-1 block text-xs text-[hsl(var(--ui-muted-foreground))]" htmlFor="login-password">
                Пароль
              </label>
              <PasswordInput
                id="login-password"
                data-testid="login-password"
                autoComplete="current-password"
                disabled={pending}
                aria-invalid={passwordErr ? true : undefined}
                aria-describedby={passwordErr ? 'login-password-err' : undefined}
                value={password}
                onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                  setPassword(ev.target.value);
                  setPasswordErr(undefined);
                  setLoginSubmitErr(undefined);
                }}
              />
              {passwordErr ? <ErrorText id="login-password-err">{passwordErr}</ErrorText> : null}
            </div>
            <SmartCaptchaNotice
              enabled={captcha.enabled}
              challengeVisible={captcha.challengeVisible}
              status={captcha.status}
              onRetry={captcha.refresh}
            />
            <Button
              type="submit"
              data-testid="login-submit"
              variant="primary"
              className="w-full"
              disabled={!canSubmitPassword || pending || captcha.loading}
              aria-invalid={loginSubmitErr ? true : undefined}
              aria-describedby={loginSubmitErr ? 'login-submit-err' : undefined}
            >
              {pending ? 'Входим…' : 'Войти'}
            </Button>
            {loginSubmitErr ? <ErrorText id="login-submit-err">{loginSubmitErr}</ErrorText> : null}
            {emailNotVerified ? (
              <div className="space-y-2 rounded-[var(--ui-radius-control)] bg-[hsl(var(--ui-muted))] p-3">
                <p className="m-0 text-sm text-[hsl(var(--ui-muted-foreground))]">
                  Необходимо подтвердить email. Мы отправили письмо со ссылкой на{' '}
                  <span className="font-medium text-[hsl(var(--ui-foreground))]">{email.trim().toLowerCase()}</span>. Проверьте почту, затем войдите по паролю.
                </p>
                {resendNote ? (
                  <p className="m-0 text-xs text-[hsl(var(--ui-muted-foreground))]" role="status" aria-live="polite">
                    {resendNote}
                  </p>
                ) : null}
                <Button
                  type="button"
                  data-testid="resend-verification-email"
                  variant="secondary"
                  className="w-full"
                  disabled={resendBusy || resendCooldown > 0}
                  onClick={() => void onResendVerificationEmail()}
                >
                  {resendBusy
                    ? 'Отправляем…'
                    : resendCooldown > 0
                      ? `Отправить письмо повторно через ${resendCooldown} сек`
                      : 'Отправить письмо повторно'}
                </Button>
              </div>
            ) : null}
          </form>
      )
    },
    ];

  return (
    <PublicOnlyRoute>
      <div className="staff-page-center relative">
        <div className="absolute right-3 top-3 z-10 sm:right-5 sm:top-5">
          <StaffThemeToggle />
        </div>
        <div className="domcrm-ui-root w-full max-w-md rounded-[var(--ui-radius)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-4 text-[hsl(var(--ui-card-foreground))] shadow-sm sm:p-6">
          {contextChoice ? (
            <div className="space-y-4" data-testid="login-context-select">
              <div>
                <h1 className="text-lg font-semibold text-[hsl(var(--ui-foreground))] sm:text-xl">Войти как</h1>
                <p className="mt-1 text-sm text-[hsl(var(--ui-muted-foreground))]">
                  У вас несколько ролей. Выберите, с какой продолжить работу.
                </p>
              </div>
              <div className="space-y-2">
                {contextChoice.contexts.map((context) => (
                  <button
                    key={context.id}
                    type="button"
                    data-testid={`login-context-option-${context.id}`}
                    disabled={contextBusyId !== null}
                    onClick={() => void onSelectContext(context)}
                    className="flex w-full items-center justify-between rounded-[var(--ui-radius-control)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] px-3 py-3 text-left text-sm transition-colors hover:bg-[hsl(var(--ui-muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring)/0.35)] disabled:opacity-60"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-[hsl(var(--ui-foreground))]">{context.label}</span>
                      {context.org_name ? (
                        <span className="mt-0.5 block truncate text-xs text-[hsl(var(--ui-muted-foreground))]">
                          {context.org_name}
                        </span>
                      ) : null}
                    </span>
                    <span className="ml-3 shrink-0 text-[hsl(var(--ui-muted-foreground))]">
                      {contextBusyId === context.id ? 'Входим…' : '→'}
                    </span>
                  </button>
                ))}
              </div>
              {contextErr ? <ErrorText>{contextErr}</ErrorText> : null}
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                disabled={contextBusyId !== null}
                onClick={() => {
                  setContextChoice(null);
                  setContextErr(undefined);
                }}
              >
                Назад ко входу
              </Button>
            </div>
          ) : twofaStep ? (
            <form className="space-y-4 min-h-[18rem] flex flex-col justify-between" onSubmit={onTotpSubmit}>
              <div>
                <h1 className="text-lg font-semibold text-[hsl(var(--ui-foreground))] sm:text-xl">Двухфакторный вход</h1>
                <p className="mt-1 text-sm text-[hsl(var(--ui-muted-foreground))]">
                  {totpRecoveryMode
                    ? 'Введите один из резервных кодов, которые выдали при включении 2FA.'
                    : 'Введите 6-значный код подтверждения из приложения-аутентификатора (Google Authenticator и др.).'}
                </p>
                <div className="mt-6">
                  <label className="mb-1 block text-xs text-[hsl(var(--ui-muted-foreground))]" htmlFor="totp-code">
                    {totpRecoveryMode ? 'Резервный код (XXXXX-XXXXX)' : 'Код подтверждения (6 цифр)'}
                  </label>
                  <Input
                    id="totp-code"
                    data-testid="totp-code"
                    type="text"
                    inputMode={totpRecoveryMode ? 'text' : 'numeric'}
                    pattern={totpRecoveryMode ? '[A-Za-z0-9-]*' : '[0-9]*'}
                    autoComplete={totpRecoveryMode ? 'off' : 'one-time-code'}
                    maxLength={totpRecoveryMode ? 11 : 6}
                    placeholder={totpRecoveryMode ? 'XXXXX-XXXXX' : '000 000'}
                    className={
                      totpRecoveryMode
                        ? 'text-center font-mono text-lg tracking-[0.15em]'
                        : 'text-center font-mono text-xl tracking-[0.5em] pl-[0.5em]'
                    }
                    value={totpCode}
                    onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                      setTotpCode(
                        totpRecoveryMode
                          ? normalizeRecoveryCodeInput(ev.target.value)
                          : ev.target.value.replace(/\D/g, '')
                      );
                      setTotpErr(undefined);
                    }}
                  />
                  {totpErr ? <ErrorText id="totp-code-err" className="mt-1">{totpErr}</ErrorText> : null}
                  <button
                    type="button"
                    data-testid="totp-recovery-toggle"
                    className="mt-2 text-xs text-[hsl(var(--ui-muted-foreground))] underline underline-offset-2 hover:text-[hsl(var(--ui-foreground))]"
                    onClick={() => {
                      setTotpRecoveryMode((v) => !v);
                      setTotpCode('');
                      setTotpErr(undefined);
                    }}
                  >
                    {totpRecoveryMode ? 'Войти с кодом из приложения' : 'Войти с резервным кодом'}
                  </button>
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <SmartCaptchaNotice
                  enabled={captcha.enabled}
                  challengeVisible={captcha.challengeVisible}
                  status={captcha.status}
                  onRetry={captcha.refresh}
                />
                <Button
                  type="submit"
                  data-testid="totp-submit"
                  variant="primary"
                  className="w-full"
                  disabled={
                    (totpRecoveryMode
                      ? totpCode.replace(/[^A-Z0-9]/g, '').length !== 10
                      : totpCode.trim().replace(/\s+/g, '').length !== 6) ||
                    pending ||
                    captcha.loading
                  }
                >
                  {pending ? 'Проверка…' : 'Подтвердить'}
                </Button>
                <Button
                  type="button"
                  data-testid="totp-back"
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    setTwofaStep(false);
                    setTotpCode('');
                    setTotpRecoveryMode(false);
                    setTotpErr(undefined);
                  }}
                >
                  Назад ко входу
                </Button>
              </div>
            </form>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-[hsl(var(--ui-foreground))] sm:text-xl">Вход</h1>
              <p className="mt-1 text-sm text-[hsl(var(--ui-muted-foreground))]">
                Войдите по звонку или с помощью пароля.
              </p>
              {contextRevoked ? (
                <p className="mt-2 rounded-[var(--ui-radius-control)] bg-[hsl(var(--ui-muted))] px-3 py-2 text-sm text-[hsl(var(--ui-foreground))]" role="alert">
                  Ваша роль изменилась, войдите заново.
                </p>
              ) : null}
              <Tabs
                dataTestId="login-tabs"
                value={tab}
                onValueChange={(v) => switchTab(v as AuthTab)}
                items={tabItems}
                panelClassName="min-h-[18rem]"
              />
            </>
          )}
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-0.5 text-xs text-[hsl(var(--ui-muted-foreground))]">
            <Link
              to={ROUTE_PATHS.publicOffer}
              className="underline underline-offset-2 hover:text-[hsl(var(--ui-foreground))]"
            >
              Публичная оферта
            </Link>
            <Link
              to={ROUTE_PATHS.tos}
              className="underline underline-offset-2 hover:text-[hsl(var(--ui-foreground))]"
            >
              Пользовательское соглашение
            </Link>
            <Link
              to={ROUTE_PATHS.gdpr}
              className="underline underline-offset-2 hover:text-[hsl(var(--ui-foreground))]"
            >
              Политика конфиденциальности
            </Link>
            <Link
              to={ROUTE_PATHS.support}
              className="underline underline-offset-2 hover:text-[hsl(var(--ui-foreground))]"
            >
              Поддержка
            </Link>
          </div>
        </div>
      </div>
    </PublicOnlyRoute>
  );
}
